import { CompetitorNearbyRadius } from '@up2stay/up2promo-api-client';
import { useCallback, useState } from 'react';

export const COMPETITOR_RADIUS_STEPS: CompetitorNearbyRadius[] = [
  CompetitorNearbyRadius._1000,
  CompetitorNearbyRadius._3000,
  CompetitorNearbyRadius._5000,
  CompetitorNearbyRadius._10000,
];

type Prefs = {
  radiusMeters: CompetitorNearbyRadius;
  /** Explicit user choice; undefined = automatic (see `useCompetitorMapPrefs`). */
  showNearbyOnMap?: boolean;
};

const DEFAULTS: Prefs = {
  radiusMeters: CompetitorNearbyRadius._3000,
};

const storageKey = (locationId: string) =>
  `up2promo:competitor-map:${locationId}`;

function read(locationId: string): Prefs {
  try {
    const raw = localStorage.getItem(storageKey(locationId));
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      radiusMeters: COMPETITOR_RADIUS_STEPS.includes(
        parsed.radiusMeters as CompetitorNearbyRadius,
      )
        ? (parsed.radiusMeters as CompetitorNearbyRadius)
        : DEFAULTS.radiusMeters,
      showNearbyOnMap:
        typeof parsed.showNearbyOnMap === 'boolean'
          ? parsed.showNearbyOnMap
          : undefined,
    };
  } catch {
    return DEFAULTS;
  }
}

/**
 * Per-location, per-device view preferences for the competitors map: search
 * radius and whether nearby businesses are drawn on the map. Client-side only —
 * nothing is persisted on the server.
 *
 * The map switch is automatic until the user touches it: on while nothing is
 * tracked (discovery), off once at least one competitor is tracked.
 */
export function useCompetitorMapPrefs(
  locationId: string,
  trackedCount: number,
) {
  const [state, setState] = useState(() => ({
    locationId,
    prefs: read(locationId),
  }));
  // Re-read when the selected location changes.
  const prefs =
    state.locationId === locationId ? state.prefs : read(locationId);

  const update = useCallback(
    (patch: Partial<Prefs>) => {
      const next = { ...read(locationId), ...patch };
      try {
        localStorage.setItem(storageKey(locationId), JSON.stringify(next));
      } catch {
        // Private mode / blocked storage: the preference just lasts this visit.
      }
      setState({ locationId, prefs: next });
    },
    [locationId],
  );

  return {
    radiusMeters: prefs.radiusMeters,
    showNearbyOnMap: prefs.showNearbyOnMap ?? trackedCount === 0,
    setRadiusMeters: (radiusMeters: CompetitorNearbyRadius) =>
      update({ radiusMeters }),
    setShowNearbyOnMap: (showNearbyOnMap: boolean) =>
      update({ showNearbyOnMap }),
  };
}
